import useSWR from 'swr'
import { TRADE_SOCKET } from '../../constants/LOCALKEYS'

import throttle from 'lodash/throttle'
import { useCallback, useEffect, useState, useRef } from 'react'
import { useToken } from '../auth/token'
import { useChartDataFeed } from './chartDataFeed'
import { useExchangeStore } from './exchangeStore'
import { useIndexPrice } from './indexprice'
import { useTradeStore } from './tradeStore'

import { useThemeStore } from '../stateStore'
import API from '../../../utils/api'
import { LASTPRICE, TRADEAPI_URL } from '../../constants/TRADEAPIKEYS'

// WebSocket 인스턴스 생성
let tradeWS: WebSocket | null = null

// 재연결 관련 변수
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 10
const BASE_RECONNECT_DELAY = 1000 // 1초
let reconnectTimeout: any = null
let pingInterval: any = null

let current: any = null
let symbol: any = null

let isResize = false

let lastprice = new Map()

let EPSALL: any = []
let currentToken: string = ''
let currentMutateIndexPrice: any = null  // UI 갱신용 함수 참조

// 웹소켓 연결 시 API에서 모든 종목의 최신 가격을 가져와서 lastprice Map 초기화
// mutateCallback: UI 갱신용 콜백 함수
const initializeLastPrices = async (mutateCallback?: (price: any, epId: number) => void) => {
	if (!EPSALL || EPSALL.length === 0 || !currentToken) return

	try {
		// 모든 종목에 대해 병렬로 API 호출
		const promises = EPSALL.map(async (ep: any) => {
			try {
				const response = await API.get(
					LASTPRICE + '?ep_id=' + ep.id,
					currentToken,
					{},
					false,
					TRADEAPI_URL
				)
				if (response?.result?.[0]?.price) {
					const price = response.result[0].price
					lastprice.set(ep.id, price)
					console.log(`[initLastPrice] ep_id=${ep.id} (${ep.pairs}): ${price}`)

					// UI 갱신 콜백 호출
					if (mutateCallback) {
						mutateCallback([{ price }, { price }], ep.id)
					}
				}
			} catch (e) {
				// 개별 종목 실패는 무시
			}
		})
		await Promise.all(promises)
		console.log('[initLastPrice] 초기화 완료, 총', lastprice.size, '개 종목')
	} catch (e) {
		console.error('[initLastPrice] 초기화 실패:', e)
	}
}
let lastcall = new Map()
let lastcall2: any = null

let joinedgroup: any = new Set()

let restarttimeout: any = null

// 종목별로 쓰로틀을 관리할 Map을 생성합니다.
const throttleMap = new Map()

const joinGroup = (sym?: string) => {
	//20자리 랜덤숫자
	let randompassword = 'P' + Math.random().toString(36).substr(2, 20)
	// 랜덤한위치에 401이라는 텍스트 삽입
	randompassword =
		randompassword.substr(
			0,
			Math.floor(Math.random() * randompassword.length)
		) +
		'401' +
		randompassword.substr(
			Math.floor(Math.random() * randompassword.length),
			randompassword.length
		)

	if (sym) {
		const now = new Date().getTime()
		if (lastcall.has(sym)) {
			if (now - lastcall.get(sym) < 1000) {
				//console.log('SKIP', now - lastcall.get(sym))
				return
			}
		}
		lastcall.set(sym, now)
	} else {
		const now = new Date().getTime()
		if (lastcall2) {
			if (now - lastcall2 < 100) {
				//console.log('SKIP', now - lastcall.get(sym))
				return
			}
		}
		lastcall2 = now
	}

	if (tradeWS != null) {
		if (tradeWS && tradeWS.readyState === WebSocket.OPEN) {
			const array = Array.from(joinedgroup)

			const json = {
				type: 'join2',
				symbol: array?.[0] || '',
				symbols: array,
				password: randompassword,
			}

			tradeWS.send(JSON.stringify(json))

			restarttimeout = null
		} else {
			if (restarttimeout == null) {
				restarttimeout = setTimeout(() => {
					joinGroup(sym)
				}, 3000)
			}
		}
	} else {
		if (restarttimeout == null) {
			restarttimeout = setTimeout(() => {
				joinGroup(sym)
			}, 3000)
		}
	}
}

export function useTradeSocket(url?: string) {
	const { data: EPS } = useExchangeStore()

	if (EPS?.length > 0) {
		EPSALL = EPS
	}

	const { data: token } = useToken()

	// 전역 변수에 토큰 저장 (onopen에서 API 호출용)
	if (token) {
		currentToken = token
	}
	const [fps, setFps] = useState(0)

	const [changedSymbol, setChangedSymbol] = useState(null)

	const { data: themeStore } = useThemeStore()
	const { data: tradeStore } = useTradeStore()

	current = tradeStore?.ep_id
	symbol = tradeStore?.pairs

	isResize = themeStore?.isResize

	const { data: updatechart } = useChartDataFeed()

	const { mutate: mutateIndexPrice } = useIndexPrice(current || EPS?.[0]?.id)

	// 전역 변수에 mutateIndexPrice 저장 (onopen에서 사용)
	currentMutateIndexPrice = mutateIndexPrice

	const handleTradePrice = useCallback(
		async trade => {
			if (!trade?.ep_id) return

			let throttleFn
			// 해당 종목의 쓰로틀 함수가 이미 존재하는지 확인합니다.
			if (throttleMap.has(trade.ep_id)) {
				// 이미 쓰로틀 함수가 존재하면 그 함수를 가져옵니다.
				throttleFn = throttleMap.get(trade.ep_id)
			} else {
				// 쓰로틀 함수가 없으면 새로 생성합니다.
				throttleFn = throttle(async () => {
					mutateIndexPrice(
						[
							{ price: lastprice.get(trade.ep_id) || 0 },
							{ price: lastprice.get(trade.ep_id) || 0 },
						],
						trade.ep_id
					)
				}, 500 - fps)
				// 새로 생성한 쓰로틀 함수를 Map에 저장합니다.
				throttleMap.set(trade.ep_id, throttleFn)
			}

			// 쓰로틀 함수를 호출합니다.
			await throttleFn()
		},
		[lastprice]
	)

	const handleCurrentTradePrice = useCallback(async trade => {
		if (!trade?.ep_id) return
		mutateIndexPrice(
			[
				{ price: lastprice.get(trade?.ep_id) || 0 },
				{ price: lastprice.get(trade?.ep_id) || 0 },
			],
			trade.ep_id
		)
	}, [])

	const handleCurrentTradePriceMobile = useCallback(
		throttle(async trade => {
			if (!trade?.ep_id) return
			mutateIndexPrice(
				[
					{ price: lastprice.get(trade?.ep_id) || 0 },
					{ price: lastprice.get(trade?.ep_id) || 0 },
				],
				trade.ep_id
			)
		}, 250 - fps + (isResize ? 150 : 0)),
		[lastprice, fps, isResize]
	)

	// PROCESS TX COMMON
	const processTX = useCallback(
		(trade: any, throttle: any) => {
			lastprice.set(trade.ep_id, trade.price)
			handleTradePrice(trade)
		},
		[updatechart]
	)

	// PROCESS T
	const processT = useCallback(
		(getDATA: any, throttle: any) => {
			const tempsplit = getDATA.substring(14).split('|')

			const ep_id = EPSALL?.find(item => item?.pairs == tempsplit[0])?.id

			const trade = {
				timestamp: +getDATA.substring(1, 14),
				ep_id: +ep_id,
				price: +tempsplit[1],
				amount: +tempsplit[2],
				cgubun: tempsplit[3] || '',
			}

			processTX(trade, throttle)
		},
		[processTX]
	)

	// PROCESS X
	const processX = useCallback(
		(getDATA: any, throttle: any) => {
			const tempsplit = getDATA.substring(1).split('!')

			const eptext = EPSALL?.find(
				(item: any) => item?.pairs == tempsplit[0]
			)?.id

			let high: any = null
			let low: any = null
			const temparray = tempsplit[1].split('@')
			temparray?.map((i: any) => {
				const temp2 = i.split('|')
				const trade = {
					timestamp: +temp2[0],
					ep_id: +eptext,
					price: +temp2[1],
					amount: +temp2[2],
					cgubun: temp2[3] || '',
				}
				if (high == null || high < trade.price) high = trade.price
				if (low == null || low > trade.price) low = trade.price

				processTX(trade, throttle)
				//console.log('X', trade)
				return trade
			})
		},
		[processTX]
	)

	const { data, mutate } = useSWR<any>(
		TRADE_SOCKET,
		async () => {
			if (tradeWS && symbol && tradeWS?.readyState === WebSocket.OPEN) {
				joinGroup(symbol)
			}

			const dataProcess = (getDATA: any) => {
				if (getDATA == null) return

				if (getDATA[0] == 'T') {
					processT(getDATA, isResize)
				} else if (getDATA[0] == 'X') {
					processX(getDATA, isResize)
				} else if (getDATA[0] == 'O') {
				} else if (getDATA[0] == 'S') {
				} else if (getDATA?.substring(0, 2) == 'CO') {
				} else {
				}
			}

			const onmessage = async (data: any) => {
				let getDATA: any = null
				try {
					getDATA = await data?.data.text()
				} catch (e) {
					try {
						//blob to string
						const reader = new FileReader()
						reader.onload = function () {
							getDATA = reader.result
							dataProcess(getDATA)
						}
						reader.readAsText(data?.data)

						return
					} catch (e) {
						console.log('onmessage error2', e, data?.data)
						return
					}
				}

				dataProcess(getDATA)
			}

			if (tradeWS == null) {
				try {
					// 차트 소켓 URL (모든 브랜드 공통) - WebFront와 동일하게 test 서버 사용
					const socketUrl = 'wss://test.globaltradechart.trade'
					console.log('START SOCKET', socketUrl)
					tradeWS = new WebSocket(socketUrl)
					tradeWS.binaryType = 'blob'

					tradeWS.onclose = (event) => {
						console.log('@@@@@@@@@@@tradeWS close@@@@@@@@@@@@@', event.code, event.reason)

						// ping interval 정리
						if (pingInterval) {
							clearInterval(pingInterval)
							pingInterval = null
						}

						tradeWS = null

						// 지수 백오프 재연결
						if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
							const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), 30000) // 최대 30초
							console.log(`재연결 시도 ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS}, ${delay}ms 후...`)

							if (reconnectTimeout) {
								clearTimeout(reconnectTimeout)
							}

							reconnectTimeout = setTimeout(() => {
								reconnectAttempts++
								mutate()
							}, delay)
						} else {
							console.error('최대 재연결 시도 횟수 초과')
							// 30초 후 재연결 카운터 리셋하고 다시 시도
							setTimeout(() => {
								reconnectAttempts = 0
								mutate()
							}, 30000)
						}
					}

					tradeWS.onerror = (error) => {
						console.log('tradeWS error', error)
					}

					tradeWS.onopen = async () => {
						console.log('tradeWS open - 연결 성공')
						reconnectAttempts = 0 // 연결 성공 시 카운터 리셋

						if (reconnectTimeout) {
							clearTimeout(reconnectTimeout)
							reconnectTimeout = null
						}

						// 웹소켓 연결 시 API에서 최신 가격 가져와서 lastprice Map 초기화
						// 장마감 시에도 Redis의 종가가 표시되도록 함
						await initializeLastPrices(currentMutateIndexPrice)

						joinGroup(symbol)

						// 30초마다 연결 상태 확인 (Cloudflare 타임아웃 방지)
						if (pingInterval) {
							clearInterval(pingInterval)
						}
						pingInterval = setInterval(() => {
							if (tradeWS && tradeWS.readyState === WebSocket.OPEN) {
								// 빈 메시지로 연결 유지
								try {
									tradeWS.send('ping')
								} catch (e) {
									console.log('ping 전송 실패', e)
								}
							}
						}, 30000) // 30초마다
					}
				} catch (e) {
					console.log('tradeWS error', e)
				}
			}

			if (tradeWS != null) {
				tradeWS.onmessage = onmessage
			}

			return tradeWS
		},
		{
			revalidateIfStale: true,
			revalidateOnFocus: true,
			revalidateOnReconnect: true,
			dedupingInterval: 0,
		}
	)

	const joincurrent = useCallback(() => {
		if (tradeWS != null) {
			if (tradeWS?.readyState === WebSocket.OPEN) {
				if (symbol) {
					if (!joinedgroup.has(symbol)) {
						joinedgroup.add(symbol)
						joinGroup(symbol)
					}
				}
			}
		} else {
			mutate()
		}
	}, [symbol, tradeWS, token])

	useEffect(() => {
		if (tradeStore?.pairs == null) return
		if (tradeStore?.pairs != changedSymbol) {
			joinedgroup.delete(changedSymbol)
			joinedgroup.add(tradeStore?.pairs)
			if (lastcall.has(tradeStore?.pairs)) {
				lastcall.delete(tradeStore?.pairs)
			}
			if (lastcall.has(changedSymbol)) {
				lastcall.delete(changedSymbol)
			}
			joinGroup(tradeStore?.pairs)
			setChangedSymbol(tradeStore?.pairs)
		}
	}, [tradeStore?.pairs, changedSymbol])

	return {
		data,
		users: [],
		updateLastTrade: () => {},
		mutate: () => {
			return mutate()
		},
		saveLog: (data: any) => {},
		reset() {
			console.log('@RESET')
			current = null
			setTimeout(() => {
				current = null
			}, 500)
		},
		send: (data: any) => {},
		joinGroup: (group: string, ep_id?: string) => {
			const symbol = EPSALL?.find(item => item?.id == ep_id)?.pairs
			if (!symbol) return
			if (symbol) {
				if (!joinedgroup.has(symbol)) {
					joinedgroup.add(symbol)
					joinGroup(symbol)
				}
			}

			return mutate()
		},
		leaveGroup: (group: string, ep_id?: string) => {
			try {
				const symbol = EPSALL?.find(item => item?.id == ep_id)?.pairs
				if (symbol) {
					if (joinedgroup.has(symbol)) {
						joinedgroup.delete(symbol)
						joinGroup(symbol)
					}
				}
				return mutate()
			} catch (e) {
				console.error(e)
			}
		},
	}
}
